import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthProvider';
import SignInScreen from './pages/auth/SignInScreen';
import SignUp from './pages/auth/SignUpScreen';
import ForgotPasswordScreen from './pages/auth/ForgotPasswordScreen';
import VerifyEmail from './pages/auth/VerifyEmail';
import ResetPassword from './pages/auth/ResetPassword';
import DashboardScreen from './pages/employer/DashboardScreen';
import AgencyDashboard from './pages/agency/AgencyDashboard';
import NotFound from './pages/not-found/NotFound';
import EmailVerificationMiddleware from './components/EmailVerificationMiddleware';
import TenantRoute from './components/TenantRoute';
import RouteGuard from './components/RouteGuard';

const PrivateRoute = ({ children }) => {
  const { currentUser } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  return (
    <EmailVerificationMiddleware>
      {children}
    </EmailVerificationMiddleware>
  );
};

const TenantBasedRedirect = () => {
  const { tenantType } = useAuth();
  
  // Redirect based on tenant type
  if (tenantType === 'agency') {
    return <Navigate to="/agency" replace />;
  } else if (tenantType === 'employer') {
    return <Navigate to="/employer" replace />;
  }
  
  // If no tenant type, redirect to login
  return <Navigate to="/login" replace />;
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <RouteGuard>
          <Routes>
          {/* Public routes */}
          <Route path="/login" element={<SignInScreen />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Employer routes - only accessible by employer tenant */}
          <Route
            path="/employer/*"
            element={
              <PrivateRoute>
                <TenantRoute allowedTenants={['employer']}>
                  <DashboardScreen />
                </TenantRoute>
              </PrivateRoute>
            }
          />
          
          {/* Agency routes - only accessible by agency tenant */}
          <Route
            path="/agency/*"
            element={
              <PrivateRoute>
                <TenantRoute allowedTenants={['agency']}>
                  <AgencyDashboard />
                </TenantRoute>
              </PrivateRoute>
            }
          />
          
          {/* Root redirect based on tenant */}
          <Route 
            path="/" 
            element={
              <PrivateRoute>
                <TenantBasedRedirect />
              </PrivateRoute>
            } 
          />
          
          {/* Catch all - 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </RouteGuard>
      </AuthProvider>
    </Router>
  );
};

export default App;
